import React, { useState, ChangeEvent, DragEvent, useEffect } from "react";
import { Conversion, ImageFormat } from "./types";
// import './ImageFormatConverter.css'
import { Box } from "./imageFormaterStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const ImageFormateConverter: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [sourceFormat, setSourceFormat] = useState<ImageFormat>("jpg");
  const [targetFormat, setTargetFormat] = useState<ImageFormat>("png");
  const [error, setError] = useState<string>("");

  const getAcceptedMimeTypes = (format: ImageFormat): string[] => {
    switch (format) {
      case "jpg":
        return ["image/jpeg", "image/jpg"];
      case "png":
        return ["image/png"];
      case "webp":
        return ["image/webp"];
      case "avif":
        return ["image/avif"];
      default:
        return ["image/*"];
    }
  };

  const handleFiles = (newFiles: FileList) => {
    const acceptedMimeTypes = getAcceptedMimeTypes(sourceFormat);
    const validFiles = Array.from(newFiles).filter((file) => {
      const isValidFormat = acceptedMimeTypes.includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      if (!isValidFormat) {
        setError(
          `File ${file.name
          } is not a valid ${sourceFormat.toUpperCase()} image.`
        );
        return false;
      }
      if (!isValidSize) {
        setError(`File ${file.name} exceeds the 5MB size limit.`);
        return false;
      }
      return true;
    });

    setFiles((prev) => [...prev, ...validFiles]);
    setConversions((prev) => [
      ...prev,
      ...validFiles.map((file) => ({
        id: Math.random().toString(36).substr(2, 9),
        fileName: file.name,
        status: "pending" as const,
      })),
    ]);
    setError("");
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer?.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setConversions((prev) => prev.filter((_, i) => i !== index));
  };

  const convertImage = async (
    file: File,
    format: ImageFormat
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        if (ctx) {
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);

          try {
            const mimeType = `image/${format === "jpg" ? "jpeg" : format}`;
            const quality =
              format === "webp" ? 0.8 : format === "avif" ? 0.7 : 0.92;

            // Fallback for AVIF if convertToBlob is not supported
            if (format === "avif") {
              // Check if the browser supports AVIF encoding
              const testCanvas = document.createElement("canvas");
              try {
                const avifDataUrl = testCanvas.toDataURL("image/avif");
                if (!avifDataUrl.startsWith("data:image/avif")) {
                  throw new Error(
                    "AVIF format is not supported in this browser."
                  );
                }
              } catch {
                throw new Error(
                  "AVIF format is not supported in this browser. Please choose another format."
                );
              }
            }

            const dataUrl = canvas.toDataURL(mimeType, quality);
            resolve(dataUrl);
            const height = document.body.scrollHeight;
            window.parent.postMessage({ type: "setHeight", height }, "*");
          } catch (error) {
            reject(
              error instanceof Error
                ? error
                : new Error(`Failed to convert to ${format}`)
            );
          }
        } else {
          reject(new Error("Failed to get canvas context"));
        }
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleConvert = async () => {
    const pending = conversions.filter(
      (conv) =>
        conv.status === "pending" || !conv.formats?.includes(targetFormat)
    );

    for (const conversion of pending) {
      const file = files.find((f) => f.name === conversion.fileName);
      if (!file) continue;

      const index = conversions.findIndex((conv) => conv.id === conversion.id);
      setConversions((prev) => {
        const newConversions = [...prev];
        newConversions[index] = {
          ...newConversions[index],
          status: "converting",
        };
        return newConversions;
      });

      try {
        const result = await convertImage(file, targetFormat);
        setConversions((prev) => {
          const newConversions = [...prev];
          newConversions[index] = {
            ...newConversions[index],
            status: "completed",
            result,
            formats: [...(newConversions[index].formats || []), targetFormat],
          };
          return newConversions;
        });
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: "setHeight", height }, "*");
      } catch (error) {
        setConversions((prev) => {
          const newConversions = [...prev];
          newConversions[index] = {
            ...newConversions[index],
            status: "error",
            error: error instanceof Error ? error.message : "Conversion failed",
          };
          return newConversions;
        });
        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: "setHeight", height }, "*");
      }
    }
  };

  const handleDownloadAll = () => {
    const completed = conversions.filter(
      (conv) =>
        conv.status === "completed" && conv.formats?.includes(targetFormat)
    );

    completed.forEach((conv) => {
      if (conv.result) {
        const link = document.createElement("a");
        link.href = conv.result;
        const newExt = `.${targetFormat}`;
        link.download = conv.fileName.replace(/\.[^/.]+$/, newExt);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  const handleSourceFormatChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSourceFormat(e.target.value as ImageFormat);
    setFiles([]);
    setConversions([]);
    setError("");
  };

  const handleTargetFormatChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setTargetFormat(e.target.value as ImageFormat);
  };

  const getStatusText = (status: Conversion["status"]) => {
    switch (status) {
      case "pending":
        return "Waiting...";
      case "converting":
        return "Converting...";
      case "completed":
        return "Completed";
      case "error":
        return "Error";
      default:
        return "";
    }
  };

  const formats: ImageFormat[] = ["jpg", "png", "webp", "avif"];
  const completedCount = conversions.filter(
    (conv) =>
      conv.status === "completed" && conv.formats?.includes(targetFormat)
  ).length;
  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [files, conversions]);
  return (
    <Box
      className={`ctr-container ${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
        }`}
        style={{paddingLeft:'0',paddingRight:'0'}}
    >
      
        <div className="imgconv-wrapper">
          {!isInIframe && (
            <ToolsHeader
              title="Image Format Converter"
              description="Convert images quickly and easily between various formats to
                suit all your needs."
            />
          )}
          <div className="imgconv-header">
            {/* <h1 className="imgconv-title">Image Format Converter</h1>
            <p className="imgconv-description">
              Convert your images to different formats easily
            </p> */}
            <div className="imgconv-format-info">
              {formats.map((format) => (
                <span key={format} className="imgconv-badge">
                  {format.toUpperCase()}
                </span>
              ))}
            </div>
          </div>

          <div className="imgconv-options">
            <div className="imgconv-format-selector">
              <div>
                <label
                  htmlFor="imgconv-source-format"
                  className="imgconv-label"
                >
                  Source Format
                </label>
                <div className="imgconv-format-select-wrapper">
                  <select
                    id="imgconv-source-format"
                    className="imgconv-format-select"
                    value={sourceFormat}
                    onChange={handleSourceFormatChange}
                  >
                    {formats.map((format) => (
                      <option key={format} value={format}>
                        {format.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <span className="imgconv-label">to</span>
              <div>
                <label
                  htmlFor="imgconv-target-format"
                  className="imgconv-label"
                >
                  Target Format
                </label>
                <div className="imgconv-format-select-wrapper">
                  <select
                    id="imgconv-target-format"
                    className="imgconv-format-select"
                    value={targetFormat}
                    onChange={handleTargetFormatChange}
                  >
                    {formats.map((format) => (
                      <option key={format} value={format}>
                        {format.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div
            className="imgconv-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() =>
              document.getElementById("imgconv-file-input")?.click()
            }
          >
            <svg
              className="imgconv-dropzone-icon"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <div id="imgconv-dropzone-text" className="imgconv-dropzone-text">
              Drag and drop your {sourceFormat.toUpperCase()} images here, or
              click to browse
            </div>
            <div id="imgconv-dropzone-hint" className="imgconv-dropzone-hint">
              Only {sourceFormat.toUpperCase()} files up to 5MB are supported
            </div>
            <input
              id="imgconv-file-input"
              type="file"
              accept={getAcceptedMimeTypes(sourceFormat).join(",")}
              multiple
              onChange={handleFileInput}
              style={{ display: "none" }}
            />
          </div>

          {error && (
            <div className="imgconv-error p-3 mt-3 rounded text-danger">
              {error}
            </div>
          )}

          {files.length > 0 && (
            <>
              <div
                id="imgconv-preview-grid"
                className="imgconv-preview-grid mt-4"
              >
                {files.map((file, index) => {
                  const conversion = conversions[index];
                  const status = conversion.formats?.includes(targetFormat)
                    ? "completed"
                    : conversion.status;
                  return (
                    <div key={conversion.id} className="imgconv-preview-item">
                      <img
                        className="imgconv-preview-image"
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                      />
                      <div className="imgconv-preview-overlay">
                        {getStatusText(status)}
                      </div>
                      {conversion.error && (
                        <div className="imgconv-error-text">
                          {conversion.error}
                        </div>
                      )}
                      <button
                        className="imgconv-remove-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div id="imgconv-buttons" className="imgconv-buttons mt-4">
                <button
                  id="imgconv-convert-btn"
                  className="imgconv-button imgconv-button-primary"
                  onClick={handleConvert}
                  disabled={
                    !conversions.some(
                      (conv) =>
                        conv.status === "pending" ||
                        (conv.status === "completed" &&
                          !conv.formats?.includes(targetFormat))
                    )
                  }
                >
                  <svg
                    className="imgconv-button-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  Convert to {targetFormat.toUpperCase()}
                </button>
                <button
                  id="imgconv-download-btn"
                  className={`imgconv-button imgconv-button-success ${!completedCount ? "imgconv-hidden" : ""
                    }`}
                  onClick={handleDownloadAll}
                >
                  <svg
                    className="imgconv-button-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {completedCount > 1 ? "Download All" : "Download"}
                </button>
              </div>
            </>
          )}
        </div>
    </Box>
  );
};

export default ImageFormateConverter;
